import { useEffect, useRef, useCallback } from "react";
import { queryClient, isPublicPath } from "@/lib/queryClient";
import { clearSessionIndicator } from "@/hooks/useAuth";

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const VISIBILITY_CHECK_THRESHOLD = 30 * 1000; // Check if hidden for more than 30 seconds

// Check if user has an active session (same check as useAuth)
function hasActiveSession(): boolean {
  return localStorage.getItem('has_session') === 'true';
}

interface SessionMonitorOptions {
  enabled?: boolean;
  onSessionExpired?: () => void;
}

export function useSessionMonitor(options: SessionMonitorOptions = {}) {
  const { enabled = true, onSessionExpired } = options;
  const lastCheckRef = useRef<number>(Date.now());
  const hiddenAtRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkSession = useCallback(async () => {
    // Don't check session if user doesn't have a session indicator
    // This prevents unnecessary 401s when user is browsing as guest
    if (!hasActiveSession()) {
      return;
    }
    
    try {
      const response = await fetch("/api/auth/user", {
        credentials: "include",
      });
      
      if (response.status === 401) {
        clearSessionIndicator();
        
        const currentPath = window.location.pathname;
        
        if (!isPublicPath(currentPath)) {
          const returnUrl = window.location.pathname + window.location.search;
          if (returnUrl && returnUrl !== '/login' && returnUrl !== '/') {
            sessionStorage.setItem('auth_redirect', returnUrl);
          }
          
          onSessionExpired?.();
          
          window.location.href = '/';
        }
      }
    } catch (error) {
      // Network error - don't redirect, just log
      console.warn("[SessionMonitor] Failed to check session:", error);
    }
    
    lastCheckRef.current = Date.now();
  }, [onSessionExpired]);

  // Heartbeat interval check
  useEffect(() => {
    if (!enabled) return;

    intervalRef.current = setInterval(() => {
      checkSession();
    }, SESSION_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkSession]);

  // Visibility change listener - check when user returns to tab
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Tab is now hidden - record the time
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === "visible") {
        // Tab is now visible - check if we should verify session
        const hiddenDuration = hiddenAtRef.current 
          ? Date.now() - hiddenAtRef.current 
          : 0;
        
        // If tab was hidden for more than threshold, check session
        if (hiddenDuration > VISIBILITY_CHECK_THRESHOLD) {
          checkSession();
        }
        
        hiddenAtRef.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, checkSession]);

  return {
    checkSession,
  };
}
